import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InfoTip from '../components/InfoTip';
import { shiftMonth } from '../utils/urlParams';
import { usePeriodParams } from '../hooks/usePeriodParams';
import { SpellCheck, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { navLabel } from '../utils/navigation';
import MonthPicker from '../components/MonthPicker';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ScoreCard from '../components/hygiene/ScoreCard';
import AttentionQueue from '../components/hygiene/AttentionQueue';
import NamingBanner from '../components/hygiene/NamingBanner';
import { NamingResultRow, NamingSummary, pctTone } from '../components/hygiene/types';
import * as fmt from '../lib/format';

const ISSUE_TYPES = ['Epic', 'Story', 'Task', 'Bug', 'Sub-task', 'Design', 'DevOps', 'Automation', 'Project Roadmap', 'POC Task'];

// Hygiene: ticket titles your team can read. Score = share of titles created
// in the month with no findings; below it, the titles that need attention.
export default function HygienePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // The month, the project, the ticket type, "open only" and "only mine" live in the address, so a reload
  // or a copied link opens the same view (and a link from Compliance can open a month and a project).
  const { period, current, isCurrent, setPeriod, projectKey, setProjectKey, mine, setMine, params, update } = usePeriodParams();
  const typeInAddress = params.get('type');
  const issueType = typeInAddress && ISSUE_TYPES.includes(typeInAddress) ? typeInAddress : '';
  const setIssueType = (type: string) => update({ type: type || null });
  const openOnly = params.get('open') !== '0';
  const setOpenOnly = (on: boolean) => update({ open: on ? null : '0' });

  const [summary, setSummary] = useState<NamingSummary | null>(null);
  const [rows, setRows] = useState<NamingResultRow[]>([]);
  const [total, setTotal] = useState(0);
  const [unlinked, setUnlinked] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingRows, setLoadingRows] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const r = await apiService.getNamingSummary({ period, mine: !isAdmin && mine });
      if (r.success) {
        setSummary(r.data);
        setUnlinked(!!r.meta?.unlinked);
        setError(null);
      } else setError(r.error || 'Could not load the scores');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load the scores');
    } finally {
      setLoadingSummary(false);
    }
  }, [period, mine, isAdmin]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const r = await apiService.getNamingResults({
        period,
        project_key: projectKey ?? undefined,
        issue_type: issueType || undefined,
        open_only: openOnly,
        mine: !isAdmin && mine,
        verdict: 'attention',
        limit: 300,
      });
      if (r.success) {
        setRows(r.data ?? []);
        setTotal(r.meta?.total ?? 0);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load the queue');
    } finally {
      setLoadingRows(false);
    }
  }, [period, projectKey, issueType, openOnly, mine, isAdmin]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);
  useEffect(() => { void loadRows(); }, [loadRows]);

  const refresh = () => { void loadSummary(); void loadRows(); };
  const overall = summary?.overall;
  const projects = summary?.projects ?? [];
  const periodIsCurrent = isCurrent;

  const emptyText = useMemo(() => {
    if (mine) return unlinked ? 'Your login is not matched to a Jira account yet.' : `No titles of yours need attention for ${fmt.month(period)}.`;
    if (rows.length === 0 && total === 0 && (overall?.total ?? 0) === 0) return `No tickets were created in ${fmt.month(period)} within your scope.`;
    return `Every title created in ${fmt.month(period)} is clear${openOnly ? ' or already closed' : ''}.`;
  }, [mine, unlinked, period, rows.length, total, overall, openOnly]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/hygiene')} subtitle="Ticket titles your team can read" icon={SpellCheck} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          {!isAdmin && <NamingBanner variant="page" onShowMine={() => setMine(true)} />}

          {/* Period and filters */}
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <button type="button" aria-label="Previous month" onClick={() => setPeriod(shiftMonth(period, -1))} className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] motion-reduce:transition-none">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <MonthPicker value={period} max={current} onChange={setPeriod} />
              <button type="button" aria-label="Next month" disabled={periodIsCurrent} onClick={() => setPeriod(shiftMonth(period, 1))} className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] disabled:opacity-30 motion-reduce:transition-none">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <select aria-label="Ticket type" value={issueType} onChange={(e) => setIssueType(e.target.value)} className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100">
              <option value="">All types</option>
              {ISSUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-200" />
              Open tickets only
            </label>
            {!isAdmin && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-200" />
                Only mine
              </label>
            )}
            <div className="ml-auto flex items-center gap-3">
              {overall && overall.total > 0 && (
                <div className="text-sm text-gray-600">
                  <span className={`text-lg font-bold tabular-nums ${pctTone(overall.pct_clear)}`}>{fmt.percent(overall.pct_clear)}</span>
                  <span className="ml-1.5">clear · {overall.clear} of {overall.total} titles</span>
                  <InfoTip id="naming.clear" />
                </div>
              )}
              <button type="button" onClick={refresh} aria-label="Refresh" className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] motion-reduce:transition-none">
                <RefreshCw className={`h-4 w-4 ${loadingSummary || loadingRows ? 'animate-spin motion-reduce:animate-none' : ''}`} />
              </button>
            </div>
          </div>

          {error && <div className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200">{error}</div>}

          {/* Project scores */}
          {loadingSummary && !summary ? (
            <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-white ring-1 ring-gray-200/70 motion-reduce:animate-none" />)}
            </div>
          ) : projects.length > 0 ? (
            <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {projects.map((p) => (
                <ScoreCard key={p.project_key} score={p} selected={projectKey === p.project_key} onSelect={setProjectKey} />
              ))}
            </div>
          ) : null}

          {/* Queue */}
          <div className="mb-2 flex items-baseline justify-between">
            <div className="flex items-center">
              <h2 className="text-sm font-semibold text-gray-900">
                Needs attention{projectKey ? ` · ${projectKey}` : ''}{total > 0 ? ` · ${total} ${total === 1 ? 'ticket' : 'tickets'}` : ''}
              </h2>
              <InfoTip id="naming.attention" />
            </div>
            <div className="text-xs text-gray-500">Grouped by assignee · rename in Jira and it drops off on the next sync</div>
          </div>
          <AttentionQueue rows={rows} loading={loadingRows && rows.length === 0} emptyText={emptyText} />
          {total > rows.length && <div className="mt-2 text-center text-xs text-gray-500">Showing the latest {rows.length} of {total}. Narrow by project or type to see the rest.</div>}
        </div>
      </div>
    </div>
  );
}
